import Cookies from 'js-cookie'
import { authService } from '@/services/authService'
import { User } from '@/types/user'
import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

const ACCESS_TOKEN = 'access_token'
const REFRESH_TOKEN = 'refresh_token'

interface AuthState {
  auth: {
    user: User | null
    setUser: (user: User | null) => void
    accessToken: string
    refreshToken: string
    setTokens: (accessToken: string, refreshToken: string) => void
    resetTokens: () => void
    reset: () => void
    restoreUser: () => Promise<void>
  }
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => {
      const accessToken = Cookies.get(ACCESS_TOKEN) || ''
      const refreshToken = Cookies.get(REFRESH_TOKEN) || ''

      return {
        auth: {
          user: null,
          setUser: (user) =>
            set((state) => ({ ...state, auth: { ...state.auth, user } })),
          accessToken,
          refreshToken,
          setTokens: (accessToken, refreshToken) =>
            set((state) => {
              Cookies.set(ACCESS_TOKEN, accessToken)
              Cookies.set(REFRESH_TOKEN, refreshToken)
              return {
                ...state,
                auth: {
                  ...state.auth,
                  accessToken,
                  refreshToken,
                },
              }
            }),
          resetTokens: () =>
            set((state) => {
              Cookies.remove(ACCESS_TOKEN)
              Cookies.remove(REFRESH_TOKEN)
              return {
                ...state,
                auth: {
                  ...state.auth,
                  accessToken: '',
                  refreshToken: '',
                },
              }
            }),
          reset: () =>
            set((state) => {
              Cookies.remove(ACCESS_TOKEN)
              Cookies.remove(REFRESH_TOKEN)
              return {
                ...state,
                auth: {
                  ...state.auth,
                  user: null,
                  accessToken: '',
                  refreshToken: '',
                },
              }
            }),
          restoreUser: async () => {
            const state = useAuthStore.getState()
            if (state.auth.accessToken && !state.auth.user) {
              try {
                const userData = await authService.getMe()
                set((state) => ({
                  ...state,
                  auth: { ...state.auth, user: userData },
                }))
              } catch (_error) {
                // Reset auth if getMe fails
                set((state) => ({
                  ...state,
                  auth: {
                    ...state.auth,
                    user: null,
                    accessToken: '',
                    refreshToken: '',
                  },
                }))
              }
            }
          },
        },
      }
    },
    {
      name: 'auth-store',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        auth: {
          user: state.auth.user,
        },
      }),
      merge: (persistedState, currentState) => {
        const persisted = persistedState as Partial<AuthState>
        return {
          ...currentState,
          auth: {
            ...currentState.auth,
            ...(persisted.auth ?? {}),
          },
        }
      },
    }
  )
)

export const useAuth = () => useAuthStore((state) => state.auth)
